import useInfiniteScroll from "react-infinite-scroll-hook";
import { useTranslations } from "use-intl";

import { SingleAvailableFlightReservation } from "@/app/ui/SingleAvailableFlightReservation";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { OrderCardWithoutReservation } from "@/features/bookings/components/OrderCardWithoutReservation";
import { OrdersInCreationCard } from "@/features/bookings/components/OrdersInCreationCard";
import { useReservations } from "@/features/bookings/hooks/useReservations";
import { ReservationType } from "@/types/enums";
import { formatCurrency } from "@/utils/currency";

export const MyFlightsList = ({
  onReservationClick,
  reservationType,
  reservationTypeSwitcher,
}: {
  onReservationClick: (reservationId: string) => void;
  reservationType: ReservationType;
  reservationTypeSwitcher: React.ReactNode;
}) => {
  const t = useTranslations();
  const { data, isLoading, isFetching, isError, hasReachedEnd, fetchNextPage } =
    useReservations({ searchFilter: reservationType, service: "air" });

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      fetchNextPage();
    },
    disabled: isError,
    rootMargin: "0px 0px 40px 0px",
  });

  if (isLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="airplane" />
      </PageLoader>
    );
  }

  const flightCount = data?.pages?.[0]?.data?.count;
  const bookings =
    data?.pages?.flatMap((page) => page.data.flightReservations) || [];
  const inProgressReservationsCount =
    data?.pages?.[0]?.data?.inProgressReservationsCount;

  return (
    <>
      <div className="mb-8 flex items-center justify-between">
        {reservationTypeSwitcher}
        {flightCount ? (
          <div className="text-md font-medium">
            {t("flights.bookedFlights", { flightCount })}
          </div>
        ) : null}
      </div>

      {inProgressReservationsCount && inProgressReservationsCount > 0 ? (
        <OrdersInCreationCard count={inProgressReservationsCount} />
      ) : null}

      {bookings.length > 0 ? (
        <ul aria-label={t("flights.bookedFlightsList")} className="space-y-2">
          {bookings.map((reservation) => (
            <li key={reservation.id}>
              {reservation.status === ReservationType.REFUNDED ? (
                <OrderCardWithoutReservation
                  reservation={reservation}
                  onClick={() => onReservationClick(reservation.id)}
                />
              ) : (
                <SingleAvailableFlightReservation
                  onClick={() => onReservationClick(reservation.id)}
                  itinerary={reservation.externalReservation}
                  reservationStatus={reservation.status}
                  bottomInfo={
                    <div className="text-end text-sm font-semibold text-text-primary">
                      {formatCurrency(reservation.price, reservation.currency)}
                    </div>
                  }
                />
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {isFetching || !hasReachedEnd ? (
        <div
          className="flex items-center justify-center py-4"
          ref={sentryRef}
          aria-hidden={!isFetching}
        >
          <AnimatedLoader icon="airplane" />
        </div>
      ) : (
        <div className="flex items-center justify-center p-8 text-text-tertiary">
          {t("flights.noResults")}
        </div>
      )}
    </>
  );
};
